/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { getBraindateConversation } from '@e180/domain/lib/braindate/util';
import { getUserFirstName } from '@e180/domain/lib/user/util';

import styles from './CheckInTakeOver.style';
import CheckInButton from './CheckInButton';
import messages from 'src/shared/app/check-in/l10n/checkInTakeOverL10n';
import Markdown from 'src/shared/app/base/component/text/Markdown';
import BraindateCard from 'src/shared/app/braindate/component/card/BraindateCard';
import TakeOver from 'src/shared/app/notification/component/TakeOver';
import { getNotificationData } from 'src/shared/app/notification/util/notificationUtil';
import PodIcon from 'src/shared/domain/braindate/component/PodIcon';
import {
  getBraindatePodName,
  getBraindateMeetingPoint,
} from 'src/shared/domain/braindate/util/meetingPointUtils';
import { getBraindateOtherParticipant } from 'src/shared/domain/braindate/util/braindateUtil';

import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';
import Subheading from 'src/shared/ui/component/text/Subheading';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { BraindateEvent } from '@e180/domain/lib/event/type';
import type { User } from '@e180/domain/lib/user/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

type Props = {
  notification: NotificationType,
  event: BraindateEvent,
  selfUser: User,
  handleClick: () => void,
  handleDismiss: () => void,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const CheckInTakeOverDumb = ({
  notification,
  event,
  selfUser,
  handleDismiss,
  intl,
  handleClick,
}: Props): React.Node => {
  // $FlowIssue
  const braindate: Braindate = getNotificationData(notification);
  const podName = getBraindatePodName(braindate, event, intl.locale);
  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);

  const titleTxt = isGroupBraindate
    ? intl.formatMessage(messages.groupHeading)
    : intl.formatMessage(messages.heading, {
        firstName: getUserFirstName(
          getBraindateOtherParticipant(braindate, selfUser),
        ),
      });

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isHidden, setIsHidden] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onCheckInClick = (e: MouseEvent) => {
    e.preventDefault();

    setIsHidden(true);
  };

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    const { current: rootElt } = rootRef;

    if (target === rootElt && isHidden) {
      handleClick();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [`${classes['root-isHidden']}`]: isHidden,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <TakeOver handleDismiss={handleDismiss}>
      <div
        className={rootClasses}
        onTransitionEnd={onTransitionEnd}
        ref={rootRef}
      >
        <div className={classes.top}>
          <Subheading layoutClass={classes.heading} withWarning>
            {titleTxt}
          </Subheading>
          <BraindateCard braindate={braindate} isInactive />
        </div>
        <div>
          <div className={classes.meeting}>
            <PodIcon
              altText={podName}
              braindate={braindate}
              layoutClass={classes.icon}
            />
            <p className={classes.instructions}>
              <Markdown>
                {intl.formatMessage(messages.meetingPointDirection, {
                  podName,
                  loungeName: getBraindateMeetingPoint(
                    braindate,
                    event,
                    intl.locale,
                  ),
                })}
              </Markdown>
            </p>
          </div>
          <CheckInButton braindate={braindate} handleClick={onCheckInClick} />
        </div>
      </div>
    </TakeOver>
  );
};

export default compose(React.memo, injectIntl)(CheckInTakeOverDumb);
